import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import QuizDialog from '../components/QuizDialog.jsx';

export default function ProfilePage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['profile'],
    queryFn: api.getProfile,
  });

  const [form, setForm] = useState({
    email: '',
    phone: '',
    email_reminders_enabled: false,
    sms_reminders_enabled: false,
    reminder_cadence: 'weekly',
  });
  const [dirty, setDirty] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);

  const clearQuizMutation = useMutation({
    mutationFn: () => api.clearQuiz(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
  });

  useEffect(() => {
    if (data?.profile) {
      setForm({
        email: data.profile.email || '',
        phone: data.profile.phone || '',
        email_reminders_enabled: !!data.profile.email_reminders_enabled,
        sms_reminders_enabled: !!data.profile.sms_reminders_enabled,
        reminder_cadence: data.profile.reminder_cadence || 'weekly',
      });
      setDirty(false);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (payload) => api.updateProfile(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setDirty(false);
    },
  });

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  }

  function handleSubmit(e) {
    e.preventDefault();
    mutation.mutate(form);
  }

  function handleSignOutCompletely() {
    api.logout().finally(() => {
      try { localStorage.removeItem('ccf_invite_token'); } catch {}
      window.location.href = '/login';
    });
  }

  if (isLoading) return <div className="p-6">Loading profile...</div>;
  if (error) return <div className="p-6 text-red-600">Error: {error.message}</div>;

  const profile = data?.profile;

  return (
    <div className="max-w-2xl mx-auto p-6 overflow-y-auto h-full">
      <h1 className="text-2xl font-bold text-ccf-primary mb-1">My Profile</h1>
      <p className="text-sm text-gray-500 mb-6">
        Signed in as <span className="font-medium">{profile?.name}</span>
        {profile?.role === 'admin' && (
          <span className="ml-2 text-xs bg-ccf-primary text-white rounded px-2 py-0.5">admin</span>
        )}
      </p>

      <form onSubmit={handleSubmit} className="space-y-5 bg-white border border-gray-200 rounded p-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            placeholder="you@creightoncommunity.org"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ccf-primary"
          />
          <p className="text-xs text-gray-500 mt-1">Used for weekly prospect reminders.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => update('phone', e.target.value)}
            placeholder="602-555-1234"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ccf-primary"
          />
        </div>

        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Reminders</h3>

          <label className="flex items-center gap-3 py-2">
            <input
              type="checkbox"
              checked={form.email_reminders_enabled}
              onChange={(e) => update('email_reminders_enabled', e.target.checked)}
              className="w-4 h-4"
            />
            <div>
              <div className="text-sm text-gray-900">Email me my prospect list</div>
              <div className="text-xs text-gray-500">
                Weekly summary of prospects awaiting follow-up.
              </div>
            </div>
          </label>

          <label className="flex items-center gap-3 py-2 opacity-60">
            <input
              type="checkbox"
              checked={form.sms_reminders_enabled}
              onChange={(e) => update('sms_reminders_enabled', e.target.checked)}
              className="w-4 h-4"
              disabled
            />
            <div>
              <div className="text-sm text-gray-900 flex items-center gap-2">
                Text me my prospect list
                <span className="text-xs bg-gray-200 text-gray-600 rounded px-2 py-0.5">coming soon</span>
              </div>
              <div className="text-xs text-gray-500">Opt-in weekly SMS reminders.</div>
            </div>
          </label>

          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Reminder cadence</label>
            <select
              value={form.reminder_cadence}
              onChange={(e) => update('reminder_cadence', e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="off">Off</option>
              <option value="weekly">Weekly</option>
              <option value="daily">Daily</option>
            </select>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Interests quiz</h3>
          <p className="text-xs text-gray-500 mb-3">
            60-second quiz — the AI uses this to find <em>intersections</em> between your searches
            and the people, causes, and interests you already have skin in.
          </p>

          {profile?.profile_json?.structured ? (
            <QuizSummary
              profile={profile.profile_json}
              onEdit={() => setQuizOpen(true)}
              onClear={() => clearQuizMutation.mutate()}
              clearing={clearQuizMutation.isPending}
            />
          ) : (
            <button
              type="button"
              onClick={() => setQuizOpen(true)}
              className="text-sm bg-ccf-accent text-white rounded px-3 py-1.5 hover:bg-emerald-700 flex items-center gap-1.5"
            >
              <span>✨</span>
              <span>Take the interests quiz</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={!dirty || mutation.isPending}
            className="bg-ccf-primary text-white rounded px-4 py-2 text-sm hover:bg-blue-900 disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving...' : 'Save changes'}
          </button>
          {mutation.isSuccess && !dirty && (
            <span className="text-xs text-emerald-600">Saved</span>
          )}
          {mutation.isError && (
            <span className="text-xs text-red-600">{mutation.error.message}</span>
          )}
        </div>
      </form>

      <div className="mt-6 bg-white border border-gray-200 rounded p-5 text-sm text-gray-600">
        <h3 className="font-semibold text-gray-900 mb-2">How do I get back in later?</h3>
        <p className="mb-3">
          Your invite link keeps you logged in for 90 days. We also save it on this device as a
          backup — if your session ever expires, we'll silently log you back in.
        </p>
        <button
          onClick={handleSignOutCompletely}
          className="text-xs text-red-600 border border-red-200 rounded px-3 py-1.5 hover:bg-red-50"
        >
          Sign out completely (clear device backup)
        </button>
      </div>

      <QuizDialog
        open={quizOpen}
        onClose={() => setQuizOpen(false)}
        initial={profile?.profile_json}
      />
    </div>
  );
}

function QuizSummary({ profile, onEdit, onClear, clearing }) {
  const s = profile.structured;
  const fields = [
    ['Causes', s.causes],
    ['Warm relationships', s.industry_relationships],
    ['Interests', s.interests],
    ['Neighborhood ties', s.neighborhood_ties],
    ['Ask comfort', s.ask_comfort],
  ].filter(([, v]) => Array.isArray(v) && v.length > 0);

  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded p-4">
      {s.summary_line && (
        <p className="text-sm text-emerald-900 italic mb-3">"{s.summary_line}"</p>
      )}
      <div className="space-y-2">
        {fields.map(([label, items]) => (
          <div key={label}>
            <div className="text-xs text-emerald-800 font-medium">{label}</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {items.map((item, i) => (
                <span
                  key={i}
                  className="text-xs bg-white border border-emerald-300 text-emerald-800 rounded px-2 py-0.5"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-emerald-200">
        <button
          type="button"
          onClick={onEdit}
          className="text-xs bg-ccf-primary text-white rounded px-3 py-1.5 hover:bg-blue-900"
        >
          Edit answers
        </button>
        <button
          type="button"
          onClick={onClear}
          disabled={clearing}
          className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
        >
          {clearing ? 'Clearing...' : 'Clear profile'}
        </button>
      </div>
    </div>
  );
}
